import {
  type EditorSDK,
  type ComponentRef,
  type ComponentDefinition,
  InstallationOriginType,
  type Origin,
} from '@wix/editor-platform-sdk-types';
import { Kind, ListFormsOrder } from '@wix/ambassador-forms-v4-form/types';
import {
  type Experiments,
  type FlowAPI,
  type IHttpClient,
  type TFunction,
} from '@wix/yoshi-flow-editor';
import { type PanoramaClientForComponent } from '@wix/yoshi-flow-editor/build/cjs/exports/panorama';
import { getPanelUrl } from '@wix/yoshi-flow-editor/utils';
import { NAMESPACE } from '../constants/namespace';
import { createForm, listForms } from '@wix/ambassador-forms-v4-form/http';
import {
  FORM_TEMPLATE_NAMES,
  FormAppPreset,
  type PresetId,
  type PresetWithTemplate,
  isEcomRequiredTemplate,
  isSchedulingRequiredForPreset,
  isServicesRequiredForPreset,
  presetIdToTemplateId,
  isPresetWithTemplate,
} from '../templates/templates';
import { BLOG_APP_DEF_ID } from '../constants/app';
import { reportAddedWidget } from './bi';
import { installEcomPages } from './ecom';
import type { ErrorHandlerPublicAPI } from '@wix/fe-essentials-editor/error-handler';
import { getRestrictions } from '@wix/ambassador-forms-v4-restrictions/http';
import {
  fetchDataForFieldPopulation,
  populateFormFields,
} from './field-population';
import { installSchedulingApps } from './scheduling';
import { EXPERIMENTS } from '../constants/experiments';
import { readConfig } from '../config/config';

export const openSettingsPanel = async (
  sdk: EditorSDK,
  appToken: string,
  componentRef: ComponentRef,
) => {
  return setTimeout(
    async () =>
      sdk.editor.openSettingsPanel(appToken, {
        componentRef,
      }),
    3000,
  );
};

interface AddWidgetParams {
  appToken: string;
  sdk: EditorSDK;
  componentRef: ComponentRef;
  component: ComponentDefinition;
  flowApi: FlowAPI;
  originalComponentId?: string;
  origin?: Origin;
}

export const addWidgetToSlot = async ({
  appToken,
  sdk,
  componentRef,
  component,
  flowApi,
}: AddWidgetParams) => {
  const { httpClient, bi, errorHandler } = flowApi;
  const presetId: PresetId = 'blogSlotSubscribe';
  await sdk.components.style.update(appToken, {
    componentRef,
    style: { param_boolean_isInSlot: true },
  });

  const formLimitReached = await isFormsLimitReached({
    httpClient,
    errorHandler,
  });
  if (formLimitReached) {
    return openSettingsPanel(sdk, appToken, componentRef);
  }

  const linguistHeader = await getLinguistHeader(sdk, appToken, flowApi);
  await handlePresetTemplate({
    appToken,
    sdk,
    httpClient,
    errorHandler,
    componentRef,
    presetId,
    linguistHeader,
    experiments: flowApi.experiments,
  });

  const msid = await sdk.info.getMetaSiteId(appToken);
  reportAddedWidget({
    component,
    presetId,
    bi: bi || undefined,
    msid,
  });
};

export const addWidgetToEditor = async ({
  component,
  originalComponentId,
  appToken,
  sdk,
  componentRef,
  flowApi,
  origin,
}: AddWidgetParams) => {
  const { httpClient, bi, errorHandler } = flowApi;
  const { formId, presetId } = await readConfig({
    sdk,
    appToken,
    component: componentRef,
  });
  if (formId) {
    return;
  }

  const wasCopyPasted = Boolean(originalComponentId);
  if (
    wasCopyPasted &&
    (presetId === FormAppPreset.Blank || presetId === FormAppPreset.Existing)
  ) {
    return;
  }

  const isTemplateDesignerTemplateSelected =
    flowApi.experiments.enabled(EXPERIMENTS.TEMPLATE_DESIGNER) && presetId;
  if (
    isTemplateDesignerTemplateSelected ||
    !flowApi.experiments.enabled(EXPERIMENTS.CREATE_FORM_FROM_PRESET)
  ) {
    return;
  }

  if (presetId === FormAppPreset.Blank) {
    await handleBlankTemplate({
      appToken,
      sdk,
      httpClient,
      errorHandler,
      componentRef,
      presetId,
      origin,
    });
  } else if (presetId === FormAppPreset.Existing) {
    await openSettingsPanel(sdk, appToken, componentRef);
  } else if (presetId) {
    const formLimitReached = await isFormsLimitReached({
      httpClient,
      errorHandler,
    });
    if (formLimitReached) {
      return Promise.all([
        resetToBlank({ sdk, appToken, compRef: componentRef }).then(() =>
          refresh({ sdk, appToken }),
        ),
        showUpgradeModal(sdk, appToken),
      ]);
    }

    const linguistHeader = await getLinguistHeader(sdk, appToken, flowApi);

    const shouldInstallSchedulingApps =
      flowApi.experiments.enabled(EXPERIMENTS.WIX_SERVICES_INSTALLATION) &&
      isPresetWithTemplate(presetId) &&
      (isServicesRequiredForPreset(presetId) ||
        isSchedulingRequiredForPreset(presetId));
    const handlePresetTemplateRequirements = {
      installSchedulingApps: shouldInstallSchedulingApps
        ? installSchedulingApps({
            flowApi,
            sdk,
            hasFormsWithScheduling: isSchedulingRequiredForPreset(presetId),
            hasFormsWithServices: isServicesRequiredForPreset(presetId),
          })
        : undefined,
    };

    await Promise.all([
      handlePresetTemplate({
        appToken,
        sdk,
        httpClient,
        errorHandler,
        componentRef,
        presetId: presetId as PresetWithTemplate,
        linguistHeader,
        experiments: flowApi.experiments,
        handlePresetTemplateRequirements,
        origin,
      }),
      isEcomRequiredTemplate(presetId) && installEcomPages({ flowApi, sdk }),
    ]);
  }

  const msid = await sdk.info.getMetaSiteId(appToken);
  reportAddedWidget({
    component,
    presetId: presetId ? (presetId as PresetId) : FormAppPreset.Blank,
    bi: bi || undefined,
    msid,
  });
};

export const getIsInBlogSlot = async (
  appToken: string,
  sdk: EditorSDK,
  component: ComponentDefinition,
) => {
  const parentId = component.parent || '';
  const parentRef = await sdk.document.components.getById(appToken, {
    id: parentId,
  });

  const parent = await sdk.document.components.serialize(appToken, {
    componentRef: parentRef,
  });

  return parent.data?.appDefinitionId === BLOG_APP_DEF_ID;
};

interface SetComponentFormIdParams {
  sdk: EditorSDK;
  appToken: string;
  formId: string;
  compRef: ComponentRef;
}

export const setComponentFormId = async ({
  sdk,
  appToken,
  formId,
  compRef,
}: SetComponentFormIdParams) =>
  sdk.tpa.data.set(appToken, {
    compRef,
    scope: 'COMPONENT',
    key: 'formId',
    value: formId,
  });

export const setComponentPresetId = async ({
  presetId,
  sdk,
  appToken,
  compRef,
}: {
  presetId: string;
  sdk: EditorSDK;
  appToken: string;
  compRef: ComponentRef;
}) =>
  sdk.tpa.data.set(appToken, {
    compRef,
    scope: 'COMPONENT',
    key: 'presetId',
    value: presetId,
  });

export const clearComponentFormId = async ({
  sdk,
  appToken,
  compRef,
}: {
  sdk: EditorSDK;
  appToken: string;
  compRef: ComponentRef;
}) =>
  sdk.tpa.data
    .remove(appToken, {
      compRef,
      scope: 'COMPONENT',
      key: 'formId',
    })
    .catch(() => {
      /* ignore: throws if key does not exist */
    });

export const resetToBlank = async ({
  sdk,
  appToken,
  compRef,
}: {
  sdk: EditorSDK;
  appToken: string;
  compRef: ComponentRef;
}) => {
  await clearComponentFormId({ sdk, appToken, compRef });
  await setComponentPresetId({
    presetId: FormAppPreset.Blank,
    sdk,
    appToken,
    compRef,
  });
};

export const refresh = async ({
  sdk,
  appToken,
  shouldFetchData = false,
  reason = '',
}: {
  sdk: EditorSDK;
  appToken: string;
  shouldFetchData?: boolean;
  reason?: string;
}) => {
  await sdk.application.livePreview.refresh(appToken, {
    shouldFetchData,
    source: reason,
  });
};

interface HandleTemplateParams {
  sdk: EditorSDK;
  appToken: string;
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  componentRef: ComponentRef;
  presetId?: string;
  origin?: Origin;
}

export const handleBlankTemplate = async ({
  appToken,
  sdk,
  httpClient,
  errorHandler,
  componentRef,
  presetId,
  origin,
}: HandleTemplateParams) => {
  const { withErrorHandler, getResolvedError } = errorHandler;
  try {
    const formLimitReached = await isFormsLimitReached({
      httpClient,
      errorHandler,
    });

    if (formLimitReached) {
      return Promise.all([
        resetToBlank({ sdk, appToken, compRef: componentRef }).then(() =>
          refresh({ sdk, appToken }),
        ),
        showUpgradeModal(sdk, appToken),
      ]);
    } else if (
      origin &&
      origin.info?.type !== InstallationOriginType.AI_ASSISTANT
    ) {
      await sdk.editor.openDashboardPanel(appToken, {
        url: '/wix-forms/form',
        closeOtherPanels: presetId === undefined,
      });
    }

    const response = await withErrorHandler(
      () =>
        httpClient.request(
          listForms({
            namespace: NAMESPACE,
            paging: { limit: 1 },
            order: ListFormsOrder.CREATED_DATE_DESC,
          }),
        ),
      { errorCodesMap: {} },
    );

    if (
      response.data.forms?.[0].createdDate &&
      response.data.forms[0].createdDate.getTime() >
        new Date().getTime() - 200000 &&
      response.data.forms[0].id
    ) {
      await setComponentFormId({
        appToken,
        sdk,
        formId: response.data?.forms?.[0]?.id,
        compRef: componentRef,
      });
    }
  } catch (e) {
    const resolvedError = getResolvedError(e);
    console.error(resolvedError);
  }
};

interface HandlePresetAddedBySiteGeneratorParams {
  appToken: string;
  sdk: EditorSDK;
  componentRef: ComponentRef;
  flowApi: FlowAPI;
  origin?: Origin;
}

export const handlePresetAddedBySiteGenerator = async ({
  appToken,
  sdk,
  componentRef,
  flowApi,
  origin,
}: HandlePresetAddedBySiteGeneratorParams) => {
  const component = await sdk.document.components.serialize(appToken, {
    componentRef,
  });

  const { httpClient, bi, errorHandler } = flowApi;
  const { presetId } = await readConfig({
    sdk,
    appToken,
    component: componentRef,
  });
  if (!presetId) {
    return;
  }

  const formLimitReached = await isFormsLimitReached({
    httpClient,
    errorHandler,
  });
  if (formLimitReached) {
    return Promise.all([
      resetToBlank({ sdk, appToken, compRef: componentRef }).then(() =>
        refresh({ sdk, appToken }),
      ),
    ]);
  }

  const linguistHeader = await getLinguistHeader(sdk, appToken, flowApi);

  const shouldInstallSchedulingApps =
    flowApi.experiments.enabled(EXPERIMENTS.WIX_SERVICES_INSTALLATION) &&
    isPresetWithTemplate(presetId) &&
    (isServicesRequiredForPreset(presetId) ||
      isSchedulingRequiredForPreset(presetId));
  const handlePresetTemplateRequirements = {
    installSchedulingApps: shouldInstallSchedulingApps
      ? installSchedulingApps({
          flowApi,
          sdk,
          hasFormsWithScheduling: isSchedulingRequiredForPreset(presetId),
          hasFormsWithServices: isServicesRequiredForPreset(presetId),
        })
      : undefined,
  };

  await Promise.all([
    handlePresetTemplate({
      appToken,
      sdk,
      httpClient,
      errorHandler,
      componentRef,
      presetId: presetId as PresetWithTemplate,
      linguistHeader,
      experiments: flowApi.experiments,
      handlePresetTemplateRequirements,
      origin,
    }),
    isEcomRequiredTemplate(presetId) && installEcomPages({ flowApi, sdk }),
  ]);

  const msid = await sdk.info.getMetaSiteId(appToken);
  reportAddedWidget({
    component,
    presetId: presetId ? (presetId as PresetId) : FormAppPreset.Blank,
    bi: bi || undefined,
    msid,
  });
};

export const getLinguistHeader = async (
  sdk: EditorSDK,
  appToken: string,
  flowApi: FlowAPI,
) => {
  const language = flowApi.environment.siteLanguage;
  const locale = await sdk.document.info.getSiteRegion(appToken);
  const instance = await sdk.info.getAppInstanceId(appToken);
  return `${language}|${locale}|true|${instance}`;
};

export interface HandlePresetTemplateRequirements {
  installSchedulingApps?: Promise<void> | undefined;
}

interface HandlePresetTemplateParams extends HandleTemplateParams {
  presetId: PresetWithTemplate;
  linguistHeader: string;
  experiments: Experiments;
  handlePresetTemplateRequirements?: HandlePresetTemplateRequirements;
}

export const handlePresetTemplate = async ({
  httpClient,
  errorHandler,
  sdk,
  appToken,
  presetId,
  componentRef,
  linguistHeader,
  experiments,
  handlePresetTemplateRequirements,
}: HandlePresetTemplateParams) => {
  const formLimitReached = await isFormsLimitReached({
    httpClient,
    errorHandler,
  });

  if (formLimitReached) {
    return Promise.all([
      resetToBlank({ sdk, appToken, compRef: componentRef }).then(() =>
        refresh({ sdk, appToken }),
      ),
      showUpgradeModal(sdk, appToken),
    ]);
  } else {
    const formId = (
      await createFormFromTemplate({
        templateId: presetIdToTemplateId(presetId) ?? '',
        httpClient,
        errorHandler,
        linguistHeader,
        experiments,
        handlePresetTemplateRequirements,
      })
    )?.id;

    formId &&
      (await setComponentFormId({
        appToken,
        sdk,
        formId,
        compRef: componentRef,
      }));
  }
};

export const showUpgradeModal = async (sdk: EditorSDK, appToken: string) => {
  const metaSiteId = await sdk.info.getMetaSiteId(appToken);
  await sdk.editor.openModalPanel(appToken, {
    shouldHideHeader: true,
    url: getPanelUrl('Form', 'UpgradeModal'),
    height: 453,
    width: 600,
    initialData: { metaSiteId },
  });
};

interface ShowChooseFormPanelProps {
  editorSDK: EditorSDK;
  appToken: string;
  componentRef: ComponentRef;
  t: TFunction;
}

export const showChooseFormPanel = async ({
  editorSDK: sdk,
  appToken,
  componentRef,
  t,
}: ShowChooseFormPanelProps) => {
  await sdk.editor.openComponentPanel(appToken, {
    title: t('gfpp_chooseForm'),
    componentRef,
    url: getPanelUrl('Form', 'ChooseForm'),
    height: 528,
    width: 600,
    initialData: { componentRef },
  });
};

export const isFormsLimitReached = async ({
  httpClient,
  errorHandler,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
}) => {
  const { withErrorHandler, getResolvedError } = errorHandler;

  try {
    const restrictionsResponse = await withErrorHandler(
      () => httpClient.request(getRestrictions({})).then((res) => res.data),
      { errorCodesMap: {} },
    );
    const formsLimit = restrictionsResponse.restrictions?.formsLimit?.limit;
    const totalActiveFormCount = restrictionsResponse.totalFormCount;

    if (
      formsLimit === undefined ||
      formsLimit === null ||
      totalActiveFormCount === undefined
    ) {
      return false;
    }

    return formsLimit !== -1 && formsLimit <= totalActiveFormCount;
  } catch (e) {
    const resolvedError = getResolvedError(e);
    console.error(resolvedError);
    throw new Error('Failed to fetch restrictions');
  }
};

interface CreateFormFromTemplate {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  templateId: string;
  linguistHeader: string;
  experiments: Experiments;
  handlePresetTemplateRequirements?: HandlePresetTemplateRequirements;
}

export const createFormFromTemplate = async ({
  httpClient,
  errorHandler,
  templateId,
  linguistHeader,
  experiments,
  handlePresetTemplateRequirements,
}: CreateFormFromTemplate) => {
  const { withErrorHandler, getResolvedError } = errorHandler;

  try {
    const [listFormResponse, dataForFieldPopulation] = await Promise.all([
      withErrorHandler(
        () =>
          httpClient.request((args) => ({
            ...listForms({
              formIds: [templateId],
              namespace: NAMESPACE,
              kind: Kind.EXTENSION,
            })(args),
            headers: { 'x-wix-linguist': linguistHeader },
          })),
        { errorCodesMap: {} },
      ),
      experiments.enabled(EXPERIMENTS.WIX_SERVICES_INSTALLATION)
        ? fetchDataForFieldPopulation({
            httpClient,
            errorHandler,
            linguistHeader,
            templateId,
            handlePresetTemplateRequirements,
          })
        : {},
    ]);

    const formToCreate = listFormResponse.data.forms?.[0];
    if (experiments.enabled(EXPERIMENTS.WIX_SERVICES_INSTALLATION)) {
      await populateFormFields({
        form: formToCreate!,
        httpClient,
        errorHandler,
        linguistHeader,
        dataForFieldPopulation,
      });
    }

    const response = await withErrorHandler(
      () =>
        httpClient.request(
          createForm({
            form: {
              ...formToCreate,
              properties: {
                name: FORM_TEMPLATE_NAMES[templateId],
                disabled: false,
              },
              kind: Kind.REGULAR,
            },
          }),
        ),
      {
        errorCodesMap: {
          applicationError: {
            DUPLICATED_FIELD_TARGETS: false,
            DUPLICATED_FIELD_IDS: false,
            DUPLICATED_STEP_IDS: false,
            DUPLICATED_RULE_IDS: false,
            MISSING_FIELD_TARGETS: false,
            UNSUPPORTED_FIELD_TARGETS_NAME: false,
            MISSING_NESTED_FORM_IDS: false,
            NON_EXISTING_NESTED_FORMS: false,
            NESTED_FORM_FIELDS_MISSING: false,
            FIELD_SCHEMA_NOT_SUPPORTED: false,
            UNSUPPORTED_FORM_NAMESPACE: false,
            FORM_FIELDS_COUNT_EXCEEDED: false,
            NAMESPACE_FORMS_COUNT_EXCEEDED: false,
            NAMESPACE_DELETED_FORMS_COUNT_EXCEEDED: false,
            FORM_SIZE_EXCEEDED: false,
            DUPLICATED_PRODUCT_IDS: false,
            MISSING_PRODUCT_NAME: false,
          },
        },
      },
    );

    return response.data.form;
  } catch (e) {
    const resolvedError = getResolvedError(e);
    console.error(resolvedError);
  }
};

export interface HandleEditFormClickParams {
  componentRef: ComponentRef;
  appToken: string;
  editorSDK: EditorSDK;
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  panorama?: PanoramaClientForComponent;
}

export const handleEditFormClick = async ({
  componentRef,
  appToken,
  editorSDK,
  httpClient,
  errorHandler,
  panorama,
}: HandleEditFormClickParams) => {
  const { formId } = await readConfig({
    sdk: editorSDK,
    appToken,
    component: componentRef,
  });

  if (formId) {
    const { withErrorHandler, getResolvedError } = errorHandler;

    try {
      const response = await withErrorHandler(
        () =>
          httpClient.request(
            listForms({
              formIds: [formId],
              namespace: NAMESPACE,
              paging: { limit: 1 },
            }),
          ),
        { errorCodesMap: {} },
      );

      await gfppOpenFormComposer(
        editorSDK,
        appToken,
        response?.data?.forms?.length ? formId : '',
        panorama,
      );
    } catch (e) {
      const resolvedError = getResolvedError(e);
      console.error(resolvedError);
    }
  } else {
    const formLimitReached = await isFormsLimitReached({
      httpClient,
      errorHandler,
    });

    if (formLimitReached) {
      await showUpgradeModal(editorSDK, appToken);
    } else {
      await gfppOpenFormComposer(editorSDK, appToken, '', panorama);
    }
  }
};

const gfppOpenFormComposer = async (
  editorSDK: EditorSDK,
  appToken: string,
  formId: string = '',
  panorama?: PanoramaClientForComponent,
) => {
  await editorSDK.editor.openDashboardPanel(appToken, {
    url: `/wix-forms/form/${formId ?? ''}`,
    closeOtherPanels: false,
  });

  await editorSDK.application.livePreview.refresh(appToken, {
    shouldFetchData: false,
    source: 'gfpp_edit_form',
  });
  panorama?.transaction('open_edit_form_modal').finish();
};
